import cn from 'classnames';
import React, { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

// CSS
import styles from '@UILayer/UILayer.scss';

// REDUX
import { getAutoPlayState, getEnvironmentConfiguration, getGlobalStyles, setPopupPanel } from '@Managers/Redux/AppState/AppState'; //getPanelsState, setPanelState,
import { useAppDispatch, useAppSelector } from '@Managers/Redux/Hooks';

// TYPES
import { AUTOPLAY_STATE_DEFAULTS } from '@GameStore/Defaults/AutoPlayDefaults';
import { DOM_ID } from '@UILayer/UILayer.types';
import { TAutoPlayPanelState } from './AutoPlayPanel.types';

// MANAGERS
import { EButtonEvents } from '@Managers/Events/ButtonEvents';
import EventsCenter from '@Managers/Events/EventsCenter';

// INPUTS
import { CurrencyUtils } from '@Core/Currencies/CurrencyUtils';
import { Button } from '@UILayer/Inputs/Button';
import SliderComponent from '@UILayer/Inputs/SliderComponent/SliderComponent';
import { IPopupPanelProperties } from '../PopupPanel.types';
import AutoPlayPanelGameSpeedSelector from './AutoPlayPanel.GameSpeedSelector';
//import TextField from '@UILayer/Inputs/TextField/TextField';

const AutoPlayPanel: React.FC<IPopupPanelProperties> = ({ baseFontSize }) => {
    const [state, setState] = useReducer((state: TAutoPlayPanelState, newState: TAutoPlayPanelState) => ({ ...state, ...newState }), {
        ...AUTOPLAY_STATE_DEFAULTS,
        spinIndex: undefined,
        startAutoPlay: false,
        lossLimitFormated: '0',
        listFontSize: undefined,
        spinsLength: 0
    });

    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const gameStoreEnvironmentConfiguration = useAppSelector(getEnvironmentConfiguration);
    const gameStoreAutoPlayState = useAppSelector(getAutoPlayState);
    const globalStyles = useAppSelector(getGlobalStyles);

    useEffect(() => {
        const index = gameStoreEnvironmentConfiguration.autoPlaySettings.spinValues.indexOf(state.totalCount);

        if (gameStoreEnvironmentConfiguration.autoPlaySettings.useLossLimitSlider) {
            setState({ ...state, startAutoPlay: state.lossLimit > 0 && state.totalCount > 0 ? true : false, spinIndex: index });
        } else {
            setState({ ...state, startAutoPlay: state.totalCount > 0 ? true : false, spinIndex: index });
        }
    }, [state.lossLimit, state.totalCount]);

    useEffect(() => {
        setState({
            ...state,
            ...gameStoreAutoPlayState,
            spinIndex: !gameStoreAutoPlayState.isEnabled ? undefined : state.spinIndex,
            spinsLength: gameStoreEnvironmentConfiguration.autoPlaySettings.spinValues.length - 1
        });
    }, [gameStoreAutoPlayState]);

    function setLossLimit(value: number) {
        setState({ ...state, lossLimit: value, lossLimitFormated: CurrencyUtils.formatRawCurrencyValue(value) });
    }

    function setSpinNumber(index: number, value: number) {
        const data = state.spinIndex === index ? { spinIndex: undefined, totalCount: 0 } : { spinIndex: index, totalCount: value };
        setState({ ...state, ...data });
    }

    function startAutoPlay() {
        dispatch(setPopupPanel(false));
        const payload = { ...state, isEnabled: true } as Partial<TAutoPlayPanelState>;
        delete payload.spinIndex;
        delete payload.startAutoPlay;
        EventsCenter.emit(EButtonEvents.AUTO_START_BUTTON_PRESSED, { ...payload });
    }

    function baseFont() {
        return baseFontSize ? { fontSize: `${baseFontSize}px` } : {};
    }

    return (
        <>
            <div className={styles.popup_panel_content_inner}>
                <div className={cn(styles.popup_panel_content_group, styles.spin_list_group)}>
                    <div className={styles.popup_panel_group_header} id={DOM_ID.SPINS_TITLE} style={baseFont()}>
                        {t('NUMBER_OF_SPINS')}
                    </div>
                    <div className={cn(styles.popup_content_list, styles.spin_list)} id={DOM_ID.SPINS_LIST_CONTAINER}>
                        {gameStoreEnvironmentConfiguration.autoPlaySettings.spinValues.map((element: any, index: any) => (
                            <div
                                key={index}
                                className={cn(
                                    styles.spin_button,
                                    styles.popup_panel_list_button,
                                    globalStyles.wcaMode,
                                    index === state.spinIndex ? styles.active : ''
                                )}
                            >
                                <Button
                                    dataElement={element}
                                    key={index}
                                    id={`ui_button_spins_list_${index}`}
                                    description={`${t('WCA_SET_BET_TO')} ${element}`}
                                    className={cn(globalStyles.wcaMode)}
                                    onChange={() => setSpinNumber(index, element)}
                                    focus={index === 0 ? true : false}
                                    style={baseFont()}
                                >
                                    {element}
                                </Button>
                            </div>
                        ))}
                    </div>
                </div>

                {gameStoreEnvironmentConfiguration.allowTurbo && (
                    <div className={cn(styles.popup_panel_content_group, styles.turbo_mode_group)}>
                        <div className={styles.popup_panel_group_header} id={DOM_ID.TURBO_MODE_LABEL} style={baseFont()}>
                            {t('GAME_SPEED')}
                        </div>
                        <div>
                            <AutoPlayPanelGameSpeedSelector fontSize={baseFont()} />
                        </div>
                    </div>
                )}

                {gameStoreEnvironmentConfiguration.autoPlaySettings.useLossLimitSlider && (
                    <div className={cn(styles.popup_panel_content_group, styles.spin_loss_limit_group)}>
                        <div className={styles.popup_panel_group_header}>
                            <div className={styles.spin_loss_limit_label} style={baseFont()}>
                                {t('LOSS_LIMIT')}
                            </div>
                            <div className={styles.spin_loss_limit_value}>
                                <span className={styles.value_box}>{state.lossLimitFormated}</span>
                            </div>
                        </div>
                        <div className={styles.spin_loss_limit_slider}>
                            <SliderComponent
                                defaultValue={gameStoreAutoPlayState.lossLimit}
                                handleChange={(value) => setLossLimit(value)}
                                showValueBox={false}
                            />
                        </div>
                    </div>
                )}

                <div className={styles.popup_panel_actions}>
                    <Button
                        style={baseFont()}
                        id={DOM_ID.BUTTON_AUTOPLAY_START}
                        description={t('WCA_START_AUTOPLAY')}
                        className={cn(styles.popup_panel_action_button, styles.play_action_button, {
                            [styles.active]: state.startAutoPlay ? true : false,
                            [styles.disabled]: !state.startAutoPlay ? true : false
                        })}
                        onChange={() => startAutoPlay()}
                    >
                        {t('START')}
                    </Button>
                </div>
            </div>
        </>
    );
};

export default AutoPlayPanel;

/*
 {index === state.spinsLength ? (
                                        <>
                                            <TextField useSpanElement={true} onResizeEnd={(value: number) => setListFontsize(value)}>
                                                {element}
                                            </TextField>
                                        </>
                                    ) : (
                                        <>{element}</>
                                    )}
*/
